import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import ModalContainer from '@components/modals/ModalContainer';
import { toastSuccess } from '@components/Toast';

import EditButton from '@features/supplier/component/EditButton';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { getSupplierProductionCapacityStrengths } from '@services/supplierOnboardingService';

import ApiClient from '@lib/apiClient';

import { getNumberUnit, printApiError, validateFloatNumber, validateNumber } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

const StrengthSummary = () => {
	const [isEdit, setIsEdit] = useState(false);
	const [supplierInfo, setSupplierInfo] = useState();
	const [initialData, setInitialData] = useState();
	const dispatch = useDispatch();
	const { id } = useParams();
	const isLoading = useSelector((store) => store.loaderStore);

	useEffect(() => {
		(async () => {
			const data = await getSupplierProductionCapacityStrengths(id);
			setSupplierInfo(data);
			setInitialData(data);
		})();
	}, []);

	const handleShow = async () => {
		setIsEdit(true);
	};

	const onChangeInfo = (e) => {
		const { value, name } = e.target;
		setSupplierInfo((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const onSubmitData = async () => {
		dispatch(openLoader());
		await ApiClient.PUT(`${BASE_URL}/supplier/${id}/update-production-capacity`, supplierInfo, '', true)
			.then(async ({ data }) => {
				dispatch(closeLoader());
				if (data && data.success) {
					toastSuccess(data.message);
					const response = await getSupplierProductionCapacityStrengths(id);
					setSupplierInfo(response);
					setInitialData(response);
					setIsEdit(false);
				}
			})
			.catch(({ response }) => {
				dispatch(closeLoader());
				printApiError(response);
			});
	};

	return (
		<div>
			<div className="capacity-section section-padding hover-section">
				<h3 className="suplier-title pb-4 font-weight-bold regular-16">
					Strength Summary
					<EditButton onEditClick={handleShow} />
				</h3>
				<div className="info-items_bottom">
					<div className="single text-left same-padding-box d-flex flex-column justify-content-center">
						<p className="gray_dark_02 regular-14">Total GMV</p>
						<h3 className="title regular-18 weight-500">
							{supplierInfo?.totalGmv
								? '$' + getNumberUnit(supplierInfo?.totalGmv) + ' /yr'
								: '---'}
						</h3>
					</div>
					<div className="single text-left same-padding-box d-flex flex-column justify-content-center">
						<p className="gray_dark_02 regular-14">Preferred Order Qty</p>
						<h3 className="title regular-18 weight-500">
							{supplierInfo?.preferredOrderQty
								? supplierInfo?.preferredOrderQty + ' pcs'
								: '---'}
						</h3>
					</div>
					<div className="single text-left same-padding-box d-flex flex-column justify-content-center">
						<p className="gray_dark_02 regular-14">MOQ</p>
						<h3 className="title regular-18 weight-500">
							{supplierInfo?.moq ? supplierInfo?.moq + ' pcs' : '---'}
						</h3>
					</div>
					<div className="single text-left same-padding-box d-flex flex-column justify-content-center">
						<p className="gray_dark_02 regular-14">Fabric Base</p>
						<h3 className="title regular-18 weight-500">
							{supplierInfo?.fabricBase ? supplierInfo?.fabricBase + ' ton/month' : '---'}
						</h3>
					</div>
					<div className="single text-left same-padding-box d-flex flex-column justify-content-center">
						<p className="gray_dark_02 regular-14">Costing Lead Time</p>
						<h3 className="title regular-18 weight-500">
							{supplierInfo?.costingLeadTime ? supplierInfo?.costingLeadTime + ' Days' : '---'}
						</h3>
					</div>
				</div>
				<div className="info-items_bottom mt-3">
					<div className="single text-left same-padding-box d-flex flex-column justify-content-center">
						<p className="gray_dark_02 regular-14">Sample Dev Lead Time</p>
						<h3 className="title regular-18 weight-500">
							{supplierInfo?.sampleDevLeadTime
								? supplierInfo?.sampleDevLeadTime + ' Days'
								: '---'}
						</h3>
					</div>
					<div className="single text-left same-padding-box d-flex flex-column justify-content-center">
						<p className="gray_dark_02 regular-14">Fabric Dev Lead Time</p>
						<h3 className="title regular-18 weight-500">
							{supplierInfo?.fabricDevLeadTime
								? supplierInfo?.fabricDevLeadTime + ' Days'
								: '---'}
						</h3>
					</div>
					<div className="single text-left same-padding-box d-flex flex-column justify-content-center">
						<p className="gray_dark_02 regular-14">Monthly Costing Capacity</p>
						<h3 className="title regular-18 weight-500">
							{supplierInfo?.monthlyCostingCapacity
								? supplierInfo?.monthlyCostingCapacity + ' pcs'
								: '---'}
						</h3>
					</div>
					<div className="single text-left same-padding-box d-flex flex-column justify-content-center">
						<p className="gray_dark_02 regular-14">Monthly Sample Capacity</p>
						<h3 className="title regular-18 weight-500">
							{supplierInfo?.monthlySampleCapacity
								? supplierInfo?.monthlySampleCapacity + ' pcs'
								: '---'}
						</h3>
					</div>
				</div>
			</div>

			{isEdit && (
				<ModalContainer>
					<div className="common-popup filter-quick-search-modal quick-info-modal">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Production Capacity</h3>
							</div>
							<div className="close-btn" onClick={() => setIsEdit(false)}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>

						<div className="common-popup-body scroll-y-label">
							<div className="container px-0">
								<div className="row">
									<div className="col-md-6 pr-2">
										<div className="form-group">
											<label htmlFor="totalGmv">Total GMV</label>
											<input
												type="number"
												onKeyDown={validateFloatNumber}
												className="w-100"
												id="totalGmv"
												name="totalGmv"
												value={supplierInfo?.totalGmv}
												onChange={(e) => onChangeInfo(e)}
												placeholder="$1500/yr"
											/>
										</div>
									</div>
									<div className="col-md-6 pr-2">
										<div className="form-group">
											<label htmlFor="preferredOrderQty">Preferred Order Qty</label>
											<input
												type="number"
												onKeyDown={validateNumber}
												className="w-100"
												id="preferredOrderQty"
												name="preferredOrderQty"
												value={supplierInfo?.preferredOrderQty}
												onChange={(e) => onChangeInfo(e)}
												placeholder="5000 pcs"
											/>
										</div>
									</div>
									<div className="col-md-6 pr-2">
										<div className="form-group">
											<label htmlFor="moq">MOQ</label>
											<input
												type="number"
												onKeyDown={validateNumber}
												className="w-100"
												id="moq"
												name="moq"
												value={supplierInfo?.moq}
												onChange={(e) => onChangeInfo(e)}
												placeholder="1500 pcs"
											/>
										</div>
									</div>
									<div className="col-md-6 pr-2">
										<div className="form-group">
											<label htmlFor="fabricBase">Fabric Base</label>
											<input
												type="number"
												onKeyDown={validateFloatNumber}
												className="w-100"
												id="fabricBase"
												name="fabricBase"
												value={supplierInfo?.fabricBase}
												onChange={(e) => onChangeInfo(e)}
												placeholder="1500 ton/month"
											/>
										</div>
									</div>
									<div className="col-md-6 pr-2">
										<div className="form-group">
											<label htmlFor="costingLeadTime">Costing Lead Time</label>
											<input
												type="number"
												onKeyDown={validateNumber}
												className="w-100"
												id="costingLeadTime"
												name="costingLeadTime"
												value={supplierInfo?.costingLeadTime}
												onChange={(e) => onChangeInfo(e)}
												placeholder="2 Days"
											/>
										</div>
									</div>
									<div className="col-md-6 pr-2">
										<div className="form-group">
											<label htmlFor="sampleDevLeadTime">Sample Dev Lead Time</label>
											<input
												type="number"
												onKeyDown={validateNumber}
												className="w-100"
												id="sampleDevLeadTime"
												name="sampleDevLeadTime"
												value={supplierInfo?.sampleDevLeadTime}
												onChange={(e) => onChangeInfo(e)}
												placeholder="3 Days"
											/>
										</div>
									</div>
									<div className="col-md-6 pr-2">
										<div className="form-group">
											<label htmlFor="fabricDevLeadTime">Fabric Dev Lead Time</label>
											<input
												type="number"
												onKeyDown={validateNumber}
												className="w-100"
												id="fabricDevLeadTime"
												name="fabricDevLeadTime"
												value={supplierInfo?.fabricDevLeadTime}
												onChange={(e) => onChangeInfo(e)}
												placeholder="7 Days"
											/>
										</div>
									</div>
									<div className="col-md-6 pr-2">
										<div className="form-group">
											<label htmlFor="monthlyCostingCapacity">
												Monthly Costing Capacity
											</label>
											<input
												type="number"
												onKeyDown={validateNumber}
												className="w-100"
												id="monthlyCostingCapacity"
												name="monthlyCostingCapacity"
												value={supplierInfo?.monthlyCostingCapacity}
												onChange={(e) => onChangeInfo(e)}
												placeholder="5000 pcs"
											/>
										</div>
									</div>
									<div className="col-md-6 pr-2">
										<div className="form-group">
											<label htmlFor="monthlySampleCapacity">
												Monthly Sample Capacity
											</label>
											<input
												type="number"
												onKeyDown={validateNumber}
												className="w-100"
												id="monthlySampleCapacity"
												name="monthlySampleCapacity"
												value={supplierInfo?.monthlySampleCapacity}
												onChange={(e) => onChangeInfo(e)}
												placeholder="5000 pcs"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button
									className="button gray-outline size32"
									onClick={() => {
										setSupplierInfo(initialData);
										setIsEdit(false);
									}}
								>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									disabled={isLoading}
									onClick={() => onSubmitData()}
								>
									Submit
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
		</div>
	);
};

export default StrengthSummary;
